import React, { useState, useEffect, useRef } from "react";
import { sizes } from "./sizes";

function PrintOrderScreen({ selectedPhotos = [], onBack, startIndex = 0, quantities, setQuantities, photoOverlays, setPhotoOverlays = () => { }, croppedImages = {}, setCroppedImages = () => { }, }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageResolution, setImageResolution] = useState(null);
  const imgRef = useRef(null);
  const [imgBox, setImgBox] = useState({ width: 0, height: 0 });
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // FIX 1: Inject 'category' into allSizes so lookups (via .find) have the category data
  const allSizes = sizes.flatMap((cat) =>
    cat.items.map((item) => ({ ...item, category: cat.category }))
  );

  const startResize = useRef(null);
  const [activeCategory, setActiveCategory] = useState("");
  const sizesContainerRef = useRef(null);
  const categoryRefs = useRef({});
  const categoryScrollRef = useRef(null);
  const resizeState = useRef(null);
  const PRINT_SCALE = 80; // px per inch

  const getAspectRatioForPrint = (size) => {
    if (!size) return 1;
    const category = size.category;

    switch (category) {
      case "Square Lustre Prints":
        return 1; // 1:1 ALWAYS

      case "Panoramic":
        // Panoramic logic: generally Height / Width
        return size.heightIn / size.widthIn;

      case "Lustre Rectangular Prints":
      case "Glossy Rectangular Prints":
      case "Enlargements":
      default:
        return size.widthIn / size.heightIn;
    }
  };

  const getRealFrame = (overlay) => {
    const box = getRenderedImageBox();
    if (!overlay?.normalizedFrame || !box.width) return overlay?.frame;

    const nf = overlay.normalizedFrame;

    return {
      width: nf.width * box.width,
      height: nf.height * box.height,
      left: box.left + nf.left * box.width,
      top: box.top + nf.top * box.height,
    };
  };

  useEffect(() => {
    const container = sizesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const blocks = container.querySelectorAll(".size-category-block");

      let current = "";
      blocks.forEach((block) => {
        const rect = block.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // distance of category block from scroll container top
        const top = rect.top - containerRect.top;

        // Perfect threshold for your sticky bars
        if (top <= 140 && top > -rect.height + 140) {
          current = block.getAttribute("data-category");
        }
      });

      if (current && current !== activeCategory) {
        setActiveCategory(current);

        // Auto-scroll horizontal category bar
        const tab = document.querySelector(`.cat-tab[data-cat="${current}"]`);
        if (tab && categoryScrollRef.current) {
          tab.scrollIntoView({ behavior: "smooth", inline: "center" });
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeCategory]);

  const handleResizeStart = (e) => {
    e.stopPropagation();

    const overlay = getOverlayForCurrent();
    const current = selectedPhotos[currentIndex];
    if (!overlay?.frame || !current?.selectedSize) return;

    // This lookup works now because allSizes has category injected
    const size = allSizes.find(s => s.label === current.selectedSize);
    if (!size) return;

    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startFrame: { ...overlay.frame },
      aspect: getAspectRatioForPrint(size)
    };

    window.addEventListener("mousemove", handleResizing);
    window.addEventListener("mouseup", handleResizeEnd);
  };

  const handleResizing = (e) => {
    if (!resizeState.current) return;

    const { startX, startY, startFrame, aspect } = resizeState.current;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    // 🔥 Use BOTH dx and dy to calculate scale
    const scaleX = (startFrame.width + dx) / startFrame.width;
    const scaleY = (startFrame.height + dy) / startFrame.height;

    // 🔥 Pick dominant scale (natural feel like tools)
    const scale = Math.max(scaleX, scaleY);

    let newWidth = startFrame.width * scale;
    let newHeight = newWidth / aspect;

    // Minimum size
    const minWidth = 50;
    if (newWidth < minWidth) {
      newWidth = minWidth;
      newHeight = minWidth / aspect;
    }

    const box = getRenderedImageBox();
    const overlay = getOverlayForCurrent();
    const rotation = overlay?.rotation || 0;

    // 🔥 Clamp to image bounds dynamically adjusting for Rotation Angle
    if (rotation === 90 || rotation === 270) {
      const img_vLeft = box.left + (box.width - box.height) / 2;
      const img_vRight = box.left + (box.width + box.height) / 2;
      const img_vTop = box.top + (box.height - box.width) / 2;
      const img_vBottom = box.top + (box.height + box.width) / 2;

      let maxW = Infinity;

      // Prevent visual Right boundary overshoot
      const w1 = (img_vRight - startFrame.left) * 2 / (1 + 1 / aspect);
      if (w1 > 0 && w1 < maxW) maxW = w1;

      // Prevent visual Bottom boundary overshoot
      const w2 = (img_vBottom - startFrame.top) * 2 / (1 + 1 / aspect);
      if (w2 > 0 && w2 < maxW) maxW = w2;

      // Prevent visual Left boundary overshoot
      const factor3 = 1 - 1 / aspect;
      if (factor3 < 0) {
        const w3 = (img_vLeft - startFrame.left) * 2 / factor3;
        if (w3 > 0 && w3 < maxW) maxW = w3;
      }

      // Prevent visual Top boundary overshoot
      const factor4 = 1 / aspect - 1;
      if (factor4 < 0) {
        const w4 = (img_vTop - startFrame.top) * 2 / factor4;
        if (w4 > 0 && w4 < maxW) maxW = w4;
      }

      if (newWidth > maxW) {
        newWidth = maxW;
        newHeight = newWidth / aspect;
      }
    } else {
      if (startFrame.left + newWidth > box.left + box.width) {
        newWidth = box.left + box.width - startFrame.left;
        newHeight = newWidth / aspect;
      }

      if (startFrame.top + newHeight > box.top + box.height) {
        newHeight = box.top + box.height - startFrame.top;
        newWidth = newHeight * aspect;
      }
    }

    updateOverlayForCurrent({
      frame: {
        ...startFrame,
        width: newWidth,
        height: newHeight,
      },
    });
  };

  const handleResizeEnd = () => {
    resizeState.current = null;
    window.removeEventListener("mousemove", handleResizing);
    window.removeEventListener("mouseup", handleResizeEnd);
  };

  const getFilterStyle = (filter) => {
    switch (filter) {
      case "blackwhite":
        return "grayscale(100%)";
      case "sepia":
        return "sepia(100%)";
      default:
        return "none";
    }
  };

  // ---------- Helpers ----------
  const getRenderedImageBox = () => {
    if (!imgRef.current) return { width: 0, height: 0, top: 0, left: 0 };

    const img = imgRef.current;
    const container = img.parentElement;

    // 🔥 USE offset values (NOT getBoundingClientRect)
    const width = img.offsetWidth;
    const height = img.offsetHeight;

    // Centered image inside flex container
    const containerRect = container.getBoundingClientRect();

    const left = (container.clientWidth - width) / 2;
    const top = (container.clientHeight - height) / 2;

    return {
      width,
      height,
      left,
      top
    };
  };

  const computeOverlayFrame = (size, box, mode) => {
    if (!size || !box || box.width <= 0 || box.height <= 0)
      return { width: 0, height: 0 };
    const aspectRatio = getAspectRatioForPrint(size);


    let frameWidth = box.width;
    let frameHeight = frameWidth / aspectRatio;

    if (mode === "contain") {
      if (frameHeight > box.height) {
        frameHeight = box.height;
        frameWidth = frameHeight * aspectRatio;
      }
    } else if (mode === "cover") {
      frameHeight = box.height;
      frameWidth = frameHeight * aspectRatio;
      if (frameWidth < box.width) {
        frameWidth = box.width;
        frameHeight = frameWidth / aspectRatio;
      }
    }
    return { width: frameWidth, height: frameHeight };
  };

  const getDpiFrame = (size) => {
    if (!size) return { width: 0, height: 0 };

    // 1 inch = 150 px at 150dpi (or 300 if you prefer)
    const requiredW = size.widthIn * 150;
    const requiredH = size.heightIn * 150;

    return {
      width: requiredW,
      height: requiredH
    };
  };

  const checkSizeFit = (size) => {
    if (!imageResolution) return true;
    const requiredW = size.widthIn * 150;
    const requiredH = size.heightIn * 150;
    return (
      imageResolution.width >= requiredW &&
      imageResolution.height >= requiredH
    );
  };

  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex, selectedPhotos]);

  useEffect(() => {
    const current = selectedPhotos[currentIndex];
    if (current) {
      const img = new Image();
      img.src = current.url;
      img.onload = () =>
        setImageResolution({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => setImageResolution(null);
    } else {
      setImageResolution(null);
    }
  }, [selectedPhotos, currentIndex]);

  useEffect(() => {
    const updateBox = () => {
      if (imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        setImgBox({ width: rect.width, height: rect.height });
      }
    };
    updateBox();
    const observer = new ResizeObserver(updateBox);
    if (imgRef.current) observer.observe(imgRef.current);
    window.addEventListener("resize", updateBox);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBox);
    };
  }, [currentIndex]);

  const getOverlayForCurrent = () => {
    const current = selectedPhotos[currentIndex];
    if (!current) return null;
    return photoOverlays[current.name]?.[current.selectedSize] || null;
  };

  const updateOverlayForCurrent = (updates) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = current.selectedSize;
    if (!sizeLabel) return;

    const box = getRenderedImageBox();

    let normalizedFrame = updates.normalizedFrame;

    // 🔥 AUTO-GENERATE normalized frame if frame exists
    if (updates.frame && box.width && box.height) {
      const f = updates.frame;

      normalizedFrame = {
        left: (f.left - box.left) / box.width,
        top: (f.top - box.top) / box.height,
        width: f.width / box.width,
        height: f.height / box.height,
      };
    }

    setPhotoOverlays(prev => ({
      ...prev,
      [current.name]: {
        ...prev[current.name],
        [sizeLabel]: {
          ...prev[current.name]?.[sizeLabel],
          ...updates,
          ...(normalizedFrame && { normalizedFrame }),
        }
      }
    }));
  };

  const updateQuantity = (sizeLabel, delta) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    current.selectedSize = sizeLabel;


    // compute new per-photo quantities based on current quantities prop
    const currentQtys = quantities[current.name] || {};
    const newQtyForPhoto = {
      ...currentQtys,
      [sizeLabel]: Math.max(0, (currentQtys[sizeLabel] || 0) + delta),
    };

    // create the new global quantities object
    const newQuantities = {
      ...quantities,
      [current.name]: newQtyForPhoto,
    };

    // update global quantities
    setQuantities(newQuantities);

    // If ALL sizes for this photo are now zero → reset cropped image and overlay
    // 🔥 REMOVE overlay for THIS size if qty is now zero
    if (newQtyForPhoto[sizeLabel] === 0) {
      setPhotoOverlays(prev => {
        const copy = { ...prev };
        if (copy[current.name]?.[sizeLabel]) {
          delete copy[current.name][sizeLabel];
        }
        // clean empty photo key
        if (Object.keys(copy[current.name] || {}).length === 0) {
          delete copy[current.name];
        }
        return copy;
      });
    }

    // 🔥 If ALL sizes are zero → full cleanup (keep your existing logic)
    const allZeros = Object.values(newQtyForPhoto).every(q => q === 0);
    if (allZeros) {
      setCroppedImages(prev => {
        const copy = { ...prev };
        delete copy[current.name];
        return copy;
      });
    }
    else {
      // If not all zeros, we still want to keep overlay/size info updated like before
      const s = allSizes.find((x) => x.label === sizeLabel);
      if (!s) return;
      ensureOverlayForSize(s)
      const box = getRenderedImageBox();

      // use DPI scaling
      const f = getDpiFrame(s, box, imageResolution);
    }
  };

  const resetTransformations = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay) return;

    updateOverlayForCurrent({
      fitMode: "crop",
      hasShrinkApplied: false,
      rotation: 0,
      scale: 1,
      frame: overlay.initialFrame, // restore first-time frame
    });
  };

  const _performCropAndSave = (photo, sizeLabel, overlayInfo) => {
    const img = imgRef.current;
    if (!overlayInfo || !img || !sizeLabel) return;

    const sizeObj = allSizes.find(s => s.label === sizeLabel);
    if (!sizeObj) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const rotation = overlayInfo.rotation || 0;
    const box = getRenderedImageBox();
    const frame = overlayInfo.frame;

    const scaleX = img.naturalWidth / box.width;
    const scaleY = img.naturalHeight / box.height;

    // ✅ STEP 1: Convert frame → image space
    const relLeft = frame.left - box.left;
    const relTop = frame.top - box.top;

    let cropX = relLeft * scaleX;
    let cropY = relTop * scaleY;
    let cropW = frame.width * scaleX;
    let cropH = frame.height * scaleY;

    // ✅ STEP 2: Handle rotation CORRECTLY (THE REAL FIX)
    const radians = (rotation * Math.PI) / 180;
    const cx = img.naturalWidth / 2;
    const cy = img.naturalHeight / 2;

    // Helper: rotate point around center (INVERSE rotation)
    const rotatePoint = (x, y, angle) => {
      const dx = x - cx;
      const dy = y - cy;

      return {
        x: cx + (dx * Math.cos(angle) - dy * Math.sin(angle)),
        y: cy + (dx * Math.sin(angle) + dy * Math.cos(angle)),
      };
    };

    // Get all 4 corners of crop rect
    const p1 = rotatePoint(cropX, cropY, -radians);
    const p2 = rotatePoint(cropX + cropW, cropY, -radians);
    const p3 = rotatePoint(cropX, cropY + cropH, -radians);
    const p4 = rotatePoint(cropX + cropW, cropY + cropH, -radians);

    // Bounding box after rotation
    const xs = [p1.x, p2.x, p3.x, p4.x];
    const ys = [p1.y, p2.y, p3.y, p4.y];

    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    cropX = minX;
    cropY = minY;
    cropW = maxX - minX;
    cropH = maxY - minY;

    // ✅ STEP 3: Setup canvas
    canvas.width = cropW;
    canvas.height = cropH;

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();

    // Move to center of canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Apply rotation
    ctx.rotate(radians);

    // Draw image centered
    ctx.drawImage(
      img,
      cropX,
      cropY,
      cropW,
      cropH,
      -cropW / 2,
      -cropH / 2,
      cropW,
      cropH
    );

    ctx.restore();

    const croppedURL = canvas.toDataURL("image/jpeg", 0.95);
    const uniqueKey = `${photo.name}_${sizeLabel}`;

    setCroppedImages(prev => ({
      ...prev,
      [uniqueKey]: croppedURL
    }));
  };

  const cropCurrentImage = () => {
    const overlay = getOverlayForCurrent();
    const current = selectedPhotos[currentIndex];

    // Safety check: ensure we have an overlay, an image, and a selected size
    if (!overlay?.frame || !imgRef.current || !current?.selectedSize) return;

    // Call the reusable helper function
    _performCropAndSave(current, current.selectedSize, overlay);
  };

  const handleDone = () => {
    const currentPhoto = selectedPhotos[currentIndex];
    if (!currentPhoto) {
      onBack();
      return;
    }

    // 1. Get all size labels that have a quantity > 0 for the current photo
    const selectedSizeLabels = Object.keys(quantities[currentPhoto.name] || {})
      .filter(label => (quantities[currentPhoto.name][label] || 0) > 0);

    // 2. Loop through all ordered sizes for this photo and save the crop
    selectedSizeLabels.forEach(sizeLabel => {
      // Retrieve the size-specific overlay frame from photoOverlays state
      // Assumes photoOverlays structure: { [photoName]: { [sizeLabel]: { frame: {...} } } }
      const overlay = photoOverlays[currentPhoto.name]?.[sizeLabel];

      // 3. Perform the crop if the specific overlay frame exists
      if (overlay) {
        _performCropAndSave(currentPhoto, sizeLabel, overlay);
      }
    });

    // Go back to Review screen after processing all crops
    onBack();
  };

  const handlePrev = () => {
    const len = selectedPhotos.length;
    if (len === 0) return;

    // Crop only if a size is selected
    const hasSelectedSize = Object.values(quantities[selectedPhotos[currentIndex].name] || {}).some(q => q > 0);
    if (hasSelectedSize) {
      cropCurrentImage();
    }

    // Always move to previous photo
    setCurrentIndex((prev) => (prev - 1 + len) % len);
  };

  const handleNext = () => {
    const len = selectedPhotos.length;
    if (len === 0) return;

    // Crop only if a size is selected
    const hasSelectedSize = Object.values(quantities[selectedPhotos[currentIndex].name] || {}).some(q => q > 0);
    if (hasSelectedSize) {
      cropCurrentImage();
    }

    // Always move to next photo
    setCurrentIndex((prev) => (prev + 1) % len);
  };

  const handleCenter = () => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = current.selectedSize;
    if (!sizeLabel) return;

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const overlay = getOverlayForCurrent();
    const padding = 35;

    // Calculate the actual frame size (existing or new fit)
    const frameWidth = overlay?.frame?.width
      ? overlay.frame.width
      : box.width - padding * 2;

    const frameHeight = overlay?.frame?.height
      ? overlay.frame.height
      : box.height - padding * 2;

    // CENTER the frame inside the image
    const centeredLeft = box.left + (box.width - frameWidth) / 2;
    const centeredTop = box.top + (box.height - frameHeight) / 2;

    // Normalized frame for exporting/cropping
    const normalizedFrame = {
      left: (centeredLeft - box.left) / box.width,
      top: (centeredTop - box.top) / box.height,
      width: frameWidth / box.width,
      height: frameHeight / box.height,
    };

    updateOverlayForCurrent({
      frame: {
        width: frameWidth,
        height: frameHeight,
        left: centeredLeft,
        top: centeredTop,
      },
      initialFrame: {
        width: frameWidth,
        height: frameHeight,
        left: centeredLeft,
        top: centeredTop,
      },
      rotation: 0,
      fitMode: "manual",
      scale: 1,
      extraPadding: padding,
      normalizedFrame,
      baseImageBox: box,
    });
  };

  const handleShrinkToFit = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay?.frame || overlay.hasShrinkApplied) return;

    const selectedSize = allSizes.find(s => s.label === current.selectedSize);
    if (!selectedSize) return;

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const printAspect = getAspectRatioForPrint(selectedSize);
    const isPanoramic = selectedSize.category === "Panoramic";

    let frameWidth, frameHeight;

    if (isPanoramic) {
      // Width-first fit for panoramic
      frameWidth = box.width;
      frameHeight = frameWidth / printAspect;

      // If taller than container, clamp to height
      if (frameHeight > box.height) {
        frameHeight = box.height;
        frameWidth = frameHeight * printAspect;
      }
    } else {
      // Standard fit logic
      const imageAspect = box.width / box.height;
      if (imageAspect > printAspect) {
        frameHeight = box.height;
        frameWidth = frameHeight * printAspect;
      } else {
        frameWidth = box.width;
        frameHeight = frameWidth / printAspect;
      }
    }

    const left = box.left + (box.width - frameWidth) / 2;
    const top = box.top + (box.height - frameHeight) / 2;

    updateOverlayForCurrent({
      fitMode: "contain",
      hasShrinkApplied: true,
      frame: { width: frameWidth, height: frameHeight, left, top },
    });
  };

  const handleRotate = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay) return;

    const nextRotation = ((overlay.rotation || 0) + 90) % 360;

    // ✅ CASE 1: Shrink-to-fit → rotate normally (KEEP your logic)
    if (overlay.fitMode === "contain" && overlay.hasShrinkApplied) {
      updateOverlayForCurrent({
        rotation: nextRotation
      });
      return;
    }

    // ✅ CASE 2: Shrink OFF → ONLY rotate (DO NOT TOUCH FRAME)
    updateOverlayForCurrent({
      rotation: nextRotation
    });
  };

  const startDrag = useRef(null);
  const handleMouseDown = (e) => {
    const overlay = getOverlayForCurrent();
    if (!overlay?.frame) return;
    startDrag.current = {
      x: e.clientX,
      y: e.clientY,
      frame: { ...overlay.frame },
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!startDrag.current) return;
    const dx = e.clientX - startDrag.current.x;
    const dy = e.clientY - startDrag.current.y;

    const box = getRenderedImageBox(); // get the visible image area
    const overlay = getOverlayForCurrent();
    if (!overlay?.frame) return;

    let newLeft = startDrag.current.frame.left + dx;
    let newTop = startDrag.current.frame.top + dy;
    const rotation = overlay.rotation || 0;

    // 🔥 Check boundaries considering visual rotation changes
    if (rotation === 90 || rotation === 270) {
      const minLeft = box.left + (box.width - box.height) / 2 - (overlay.frame.width - overlay.frame.height) / 2;
      const maxLeft = box.left + (box.width + box.height) / 2 - (overlay.frame.width + overlay.frame.height) / 2;
      const minTop = box.top + (box.height - box.width) / 2 - (overlay.frame.height - overlay.frame.width) / 2;
      const maxTop = box.top + (box.height + box.width) / 2 - (overlay.frame.height + overlay.frame.width) / 2;

      if (newLeft < minLeft) newLeft = minLeft;
      if (newLeft > maxLeft) newLeft = maxLeft;
      if (newTop < minTop) newTop = minTop;
      if (newTop > maxTop) newTop = maxTop;
    } else {
      // Prevent crossing left/right
      if (newLeft < box.left) newLeft = box.left;
      if (newLeft + overlay.frame.width > box.left + box.width)
        newLeft = box.left + box.width - overlay.frame.width;

      // Prevent crossing top/bottom
      if (newTop < box.top) newTop = box.top;
      if (newTop + overlay.frame.height > box.top + box.height)
        newTop = box.top + box.height - overlay.frame.height;
    }

    updateOverlayForCurrent({
      frame: {
        ...overlay.frame,
        left: newLeft,
        top: newTop,
      },
    });
  };

  const handleMouseUp = () => {
    startDrag.current = null;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleToggleallSizes = () => {
    if (!showSelectedOnly) {
      // User wants to switch to "Show Selected allSizes"
      const selectedallSizes = allSizes.filter(
        (s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0
      );

      if (selectedallSizes.length === 0) {
        // 🚨 No allSizes selected → redirect back to all allSizes list
        setShowSelectedOnly(false); // make sure it's on full list
        return;
      }
    }

    // ✅ Toggle normally if at least one size is selected
    setShowSelectedOnly(!showSelectedOnly);
  };

  const currentPhoto = selectedPhotos[currentIndex];
  const currentOverlay = getOverlayForCurrent();
  if (!currentPhoto) {
    return (<div className="print-order-container">
      <div style={{ padding: 24 }}>No photo selected.</div> </div>
    );
  }

  const allSizesToRender = showSelectedOnly
    ? allSizes.filter((s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0)
    : allSizes;

  const ensureOverlayForSize = (size) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = size.label;
    if (photoOverlays[current.name]?.[sizeLabel]) return; // already exists

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const frame = getCenteredOverlayFrame(size, box);

    setPhotoOverlays(prev => ({
      ...prev,
      [current.name]: {
        ...prev[current.name],
        [sizeLabel]: {
          fitMode: "crop",
          rotation: 0,
          scale: 1,
          frame,
          initialFrame: frame,
        },
      },
    }));
  };

  const handleSelectSize = (size) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    current.selectedSize = size.label;

    const existingOverlay = photoOverlays[current.name]?.[size.label];
    if (existingOverlay) {
      updateOverlayForCurrent({ ...existingOverlay });
      return;
    }

    // Ensure category is passed correctly
    const fullSize = { ...size, category: size.category || size.category };

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    // Use width-first for Panoramic
    const frame = getCenteredOverlayFrame(fullSize, box);

    setPhotoOverlays(prev => ({
      ...prev,
      [current.name]: {
        ...prev[current.name],
        [size.label]: {
          fitMode: "crop",
          rotation: 0,
          scale: 1,
          frame,
          initialFrame: frame,
        },
      },
    }));
  };

  const getPrintFrameFromSize = (size) => {
    return {
      width: size.widthIn * PRINT_SCALE,
      height: size.heightIn * PRINT_SCALE,
    };
  };

  const getCenteredOverlayFrame = (size, imageBox) => {
    const aspect = getAspectRatioForPrint(size);
    const isPanoramic = size.category === "Panoramic";

    let frameWidth, frameHeight;

    if (isPanoramic) {
      // Panoramic: Initialize to FULL WIDTH
      frameWidth = imageBox.width;
      frameHeight = frameWidth / aspect;

      // If it's too tall, scale down to fit height
      if (frameHeight > imageBox.height) {
        frameHeight = imageBox.height;
        frameWidth = frameHeight * aspect;
      }
    } else {
      // Standard: 80% width logic
      frameWidth = imageBox.width * 0.8;
      frameHeight = frameWidth / aspect;
      if (frameHeight > imageBox.height * 0.8) {
        frameHeight = imageBox.height * 0.8;
        frameWidth = frameHeight * aspect;
      }
    }

    return {
      width: frameWidth,
      height: frameHeight,
      left: imageBox.left + (imageBox.width - frameWidth) / 2,
      top: imageBox.top + (imageBox.height - frameHeight) / 2,
    };
  };
  const hasSelectedSize =
    Object.values(quantities[currentPhoto.name] || {}).some(q => q > 0);

  const isShrinkToFit =
    currentOverlay?.fitMode === "contain" &&
    currentOverlay?.hasShrinkApplied === true &&
    hasSelectedSize;

  const getShrinkPreviewStyle = () => {
    if (!isShrinkToFit || !currentOverlay?.frame || !imgRef.current) {
      return {};
    }

    const img = imgRef.current;
    const frame = currentOverlay.frame;

    const imgAspect = img.naturalWidth / img.naturalHeight;
    const frameAspect = frame.width / frame.height;

    let scale = 1;

    if (imgAspect > frameAspect) {
      scale = frame.width / img.clientWidth;
    } else {
      scale = frame.height / img.clientHeight;
    }

    return {
      transform: `scale(${scale})`,
      transformOrigin: "center center",
    };
  };
  const realFrame = getRealFrame(currentOverlay);
  return (
    <div className="print-order-container"> <div className="print-order-content">
      {/* LEFT SIDE */}
      <div className="preview-column">
        <div className="print-order-topbar">
          <div className="photo-info">
            <span className="photo-index">{currentIndex + 1} of {selectedPhotos.length} photos</span>
            <span className="photo-name">{currentPhoto.name}</span>
          </div>
          <button className="print-done-btn" onClick={handleDone}>
            <i className="fa-solid fa-check"></i> Done
          </button>
        </div>

        <div className="preview-image-wrapper">
          {/* ⬅️ Prev Button (outside image left side) */}
          {selectedPhotos.length > 1 && (
            <button className="nav-outside left" onClick={handlePrev}>
              <i className="fa-solid fa-chevron-left"></i>
            </button>
          )}

          {/* 🖼️ Image Container */}
          <div
            className="image-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              overflow: "hidden",
              backgroundColor: hasSelectedSize ? "#5d5d5d" : "#2E2E2E",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              ref={imgRef}
              key={currentPhoto.name}
              src={currentPhoto.url}
              alt={currentPhoto.name}
              className="print-preview"
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "60%",
                height: "auto",
                display: "block",
                filter: getFilterStyle(currentPhoto.filterApplied),
                // 🔥 APPLY ROTATION ONLY WHEN SHRINK IS OFF
                transform: `
                  rotate(${currentOverlay?.rotation || 0}deg)
                  ${isShrinkToFit ? getShrinkPreviewStyle().transform || "" : ""}
                `,
                transformOrigin: "center center",
              }}
              draggable={false}
            />

            {/* 🟩 Crop Overlay */}
            {currentOverlay?.frame &&
              Object.values(quantities[currentPhoto.name] || {}).some(qty => qty > 0) && (

                <div
                  className="crop-overlay"
                  style={{
                    position: "absolute",
                    top: realFrame.top,
                    left: realFrame.left,
                    width: realFrame.width,
                    height: realFrame.height,
                    border: "4px solid #2962FF",
                    boxSizing: "border-box",
                    transform: `rotate(${currentOverlay.rotation || 0}deg)`,
                    transformOrigin: "center center",
                    cursor: "move",
                    overflow: "hidden", // ensure image stays inside overlay
                    backgroundColor: isShrinkToFit ? "#ffffff" : "transparent", // empty space white
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onMouseDown={handleMouseDown}
                >
                  {/* Image centered inside overlay */}
                  {isShrinkToFit && imgRef.current && (
                    <img
                      src={currentPhoto.url}
                      alt={currentPhoto.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                        filter: getFilterStyle(currentPhoto.filterApplied),
                      }}
                      draggable={false}
                    />
                  )}

                  {/* Resize Handle */}
                  {!isShrinkToFit && (
                    <div
                      className="resize-handle"
                      style={{
                        position: "absolute",
                        width: "40px",
                        height: "40px",
                        background: "#2962FF",
                        borderRadius: "3px",
                        cursor: "nwse-resize",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#fff",
                        fontSize: "12px",
                        pointerEvents: "all",

                        // -----------------------------
                        // Angle-wise positioning logic
                        // -----------------------------

                        top: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return undefined;                  // 0° → bottom-right (top not set)
                          if (r === 90) return "-2px";                    // 90° → top-right
                          if (r === 180) return "-2px";                   // 180° → top-left
                          if (r === 270) return currentOverlay.frame.height - 38; // 270° → bottom-left
                          return undefined;
                        })(),

                        bottom: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return "-2px";                     // 0° → bottom-right
                          if (r === 90) return undefined;                // 90° → top-right (bottom not set)
                          if (r === 180) return currentOverlay.frame.height - 38; // 180° → top-left
                          if (r === 270) return "-2px";                  // 270° → bottom-left
                          return "-2px";
                        })(),

                        left: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return undefined;                 // 0° → bottom-right (left not set)
                          if (r === 90) return currentOverlay.frame.width - 38;  // 90° → top-right
                          if (r === 180) return "-2px";                  // 180° → top-left
                          if (r === 270) return "-2px";                  // 270° → bottom-left
                          return undefined;
                        })(),

                        right: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return "-2px";                    // 0° → bottom-right
                          if (r === 90) return "-2px";                   // 90° → top-right
                          if (r === 180) return currentOverlay.frame.width - 38; // 180° → top-left
                          if (r === 270) return currentOverlay.frame.width - 38; // 270° → bottom-left
                          return "-2px";
                        })(),

                        transform: `rotate(${-currentOverlay.rotation || 0}deg)`,
                        transformOrigin: "center center",
                      }}
                      onMouseDown={(e) => handleResizeStart(e)}
                    >
                      <i className="fa-solid fa-crop"></i>
                    </div>
                  )}


                </div>
              )}

            {/* ⚠ Low Resolution Warning */}
            {allSizes.some(
              (s) =>
                (quantities[currentPhoto.name]?.[s.label] || 0) > 0 &&
                !checkSizeFit(s)
            ) && <div className="warning-overlay">⚠ Low Resolution</div>}
          </div>


          {/* ➡️ Next Button (outside image right side) */}
          {selectedPhotos.length > 1 && (
            <button className="nav-outside right" onClick={handleNext}>
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          )}
        </div>
        <div className="preview-controls">
          <button onClick={handleCenter}>Center</button> <button onClick={handleShrinkToFit}>Shrink to Fit</button>
          <button onClick={handleRotate}>Rotate</button> <button onClick={resetTransformations}>Reset</button>
        </div>
      </div>

      {/* RIGHT SIDE PANEL */}
      <div className="allSizes-column" ref={sizesContainerRef}>

        {/* HEADER */}
        <div className="sizes-top-title">
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginLeft: "20px",
            }}
          >
            {showSelectedOnly ? (
              <>
                <i
                  className="fa-solid fa-arrow-left"
                  style={{ cursor: "pointer", fontSize: "16px", color: "#333" }}
                  title="Show all sizes"
                  onClick={handleToggleallSizes}
                ></i>
                <span style={{ marginLeft: "6px" }}>Selected Print Sizes</span>
              </>
            ) : (
              "Select Print Sizes"
            )}
          </h3>

          {Object.values(quantities[currentPhoto.name] || {}).some((q) => q > 0) && (
            <button
              className={`sizes-toggle-btn ${showSelectedOnly ? "active" : ""}`}
              onClick={handleToggleallSizes}
            >
              {showSelectedOnly ? "Show All Sizes" : "Show Selected Sizes"}
            </button>
          )}

          {/* HORIZONTAL CATEGORY BAR */}
          {!showSelectedOnly && (
            <div className="category-bar-wrapper">
              <button
                className="cat-scroll-btn left"
                onClick={() => {
                  categoryScrollRef.current.scrollBy({ left: -150, behavior: "smooth" });
                }}
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>

              <div className="category-horizontal-bar" ref={categoryScrollRef}>
                {sizes.map((cat) => (
                  <button
                    data-cat={cat.category}
                    key={cat.category}
                    className={`cat-tab ${activeCategory === cat.category ? "active" : ""}`}
                    onClick={() => {
                      const el = categoryRefs.current[cat.category];
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                  >
                    {cat.category}
                  </button>

                ))}
              </div>

              <button
                className="cat-scroll-btn right"
                onClick={() => {
                  categoryScrollRef.current.scrollBy({ left: 150, behavior: "smooth" });
                }}
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>

        {/* ───────────────────────────────────
      BUILD CATEGORY LIST SAFELY  
      ─────────────────────────────────── */}
        {(() => {
          // 1. full category structure from sizes.js
          const allCategories = sizes;

          // 2. build filtered structure safely
          const structured = showSelectedOnly
            ? allCategories
              .map((cat) => ({
                ...cat,
                items: (cat.items || []).filter(
                  (s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0
                ),
              }))
              .filter((cat) => cat.items.length > 0)
            : allCategories;

          // 3. handle empty case
          if (!structured.length) {
            return (
              <p style={{ fontStyle: "italic", color: "#555" }}>
                No sizes selected yet.
              </p>
            );
          }

          // 4. final safe mapping
          return structured.map((cat) => (
            <div key={cat.category}
              className="size-category-block"
              data-category={cat.category}
              ref={(el) => (categoryRefs.current[cat.category] = el)}>

              {/* CATEGORY BUTTON / HEADER */}
              <h4 className="size-category-btn">{cat.category}</h4>

              {/* CATEGORY ITEMS */}
              {(cat.items || []).map((s) => {
                const qty = quantities[currentPhoto.name]?.[s.label] || 0;
                const ok = checkSizeFit(s);

                return (
                  <div
                    key={s.label}
                    className="size-row"
                    // FIX 2: Pass the category explicitly to handleSelectSize
                    onClick={() => handleSelectSize({ ...s, category: cat.category })}
                    style={{
                      backgroundColor: qty > 0 ? "#2962FF" : "transparent",
                      color: qty > 0 ? "white" : "inherit",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      marginBottom: "6px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "0.2s ease",
                    }}
                  >
                    <span>
                      {s.label} – ${s.price.toFixed(2)} each{" "}
                      {!ok && (
                        <i
                          className="fa-solid fa-triangle-exclamation"
                          style={{
                            color: "yellow",
                            textShadow: "0 0 2px black",
                            marginLeft: "8px",
                          }}
                        ></i>
                      )}
                    </span>

                    <div className="qty-controls">
                      <button
                        className={qty > 0 ? "active" : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(s.label, -1);
                        }}
                      >
                        -
                      </button>
                      <span>{qty}</span>
                      <button
                        className={qty > 0 ? "active" : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(s.label, 1);
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>

    </div>
    </div>

  );
}

export default PrintOrderScreen;